import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { DocumentTextIcon } from "@heroicons/react/24/solid";
import ZoomableImageModal from "./ZoomableImageModal";
import { cleanCreatedTimestamp } from "../utils/dateUtils";
import { isTapeReading } from '../utils/tradeHelpers';

const JournalEntryList = ({ entries, loading }) => {
  const navigate = useNavigate();
  const [modal, setModal] = useState({ open: false, src: null });
  
  // Helper to format date
  const formatDate = (entry) => {
    if (entry.month && entry.day && entry.year) {
      return `${entry.month}/${entry.day}/${entry.year}`;
    }
    if (entry.created) {
      const date = new Date(cleanCreatedTimestamp(entry.created));
      return date.toLocaleDateString();
    }
    return 'N/A';
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-6 w-full max-w-3xl mx-auto py-12">
        <div className="bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-card h-12 w-1/3 animate-pulse mb-4 shadow-subtle" />
        {[...Array(3)].map((_,i) => <div key={i} className="bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-card h-[280px] animate-pulse shadow-subtle" />)}
      </div>
    );
  }
  if (!entries.length) {
    return <div className="text-[#6B7280] dark:text-[#9CA3AF] mb-4">No entries for this day yet.</div>;
  }
  return (
    <>
      <ZoomableImageModal
        isOpen={modal.open}
        images={modal.src ? [modal.src] : []}
        index={0}
        onClose={() => setModal({ open: false, src: null })}
      />
      <div className="flex items-center gap-3 mb-6 mt-2">
        <DocumentTextIcon className="w-7 h-7 text-[#3B82F6]" />
        <span className="text-2xl font-semibold text-[#111827] dark:text-[#F9FAFB]">Journal Entries</span>
        <div className="flex-1 border-b border-[#E5E7EB] dark:border-[#374151] ml-4" />
      </div>
      <div className="flex flex-col gap-4 w-full max-w-full mb-12">
        {entries.map((entry, idx) => {
          const firstScreenshot = entry.screenshots && entry.screenshots.length > 0 ? entry.screenshots[0] : null;
          const pnlColor = entry.pnl > 0 ? 'theme-success' : entry.pnl < 0 ? 'theme-loss' : 'text-[#111827] dark:text-[#F9FAFB]';
          
          const entryId = entry.id || `${entry.created}-${entry.tickerTraded}`;
          
          return (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3, delay: idx * 0.05 }}
              className="bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-card shadow-subtle hover:shadow-hover transition-all duration-200 overflow-hidden cursor-pointer"
              style={{ height: '280px' }}
              onClick={() => {
                navigate(`/context?entryId=${encodeURIComponent(entryId)}`);
              }}
            >
              <div className="flex h-full">
                {/* Left: Chart thumbnail */}
                <div className="w-[280px] h-full bg-white dark:bg-[#1F2937] border-r border-[#E5E7EB] dark:border-[#374151] flex items-center justify-center relative" style={{ backgroundImage: 'linear-gradient(to right, var(--theme-border) 1px, transparent 1px), linear-gradient(to bottom, var(--theme-border) 1px, transparent 1px)', backgroundSize: '20px 20px' }}>
                  {firstScreenshot ? (
                    <img
                      src={firstScreenshot}
                      alt="Trade Chart"
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setModal({ open: true, src: firstScreenshot });
                      }}
                    />
                  ) : (
                    <div className="rounded-full border border-[#E5E7EB] dark:border-[#374151] bg-white dark:bg-[#1F2937] px-3 py-1 text-[#6B7280] dark:text-[#9CA3AF] text-sm">No screenshot</div>
                  )}
                </div>
                
                {/* Right: Details */}
                <div className="flex-1 p-6 flex flex-col justify-between">
                  <div>
                    {/* Title */}
                    <h3 className="font-semibold text-lg text-[#111827] dark:text-[#F9FAFB] mb-2">{entry.title || 'Untitled Trade'}</h3>
                    
                    {/* Ticker + Date */}
                    <div className="font-normal text-sm text-[#6B7280] dark:text-[#9CA3AF] mb-4">
                      {(entry.tickerTraded || entry.ticker) ? `${entry.tickerTraded || entry.ticker} • ` : ''}{formatDate(entry)}
                    </div>
                    
                    {/* P&L + R:R (hidden for tape readings) */}
                    {isTapeReading(entry) ? (
                      <div className="flex items-center gap-4 mb-4">
                        <span className="bg-[#DBEAFE] dark:bg-[#1E3A8A] text-[#3730A3] dark:text-[#93C5FD] px-3 py-1 rounded-full text-sm font-medium">
                          Tape Reading
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-4 mb-4">
                        <span className={`number-medium ${pnlColor}`}>
                          {Number(entry.pnl) > 0 ? '+' : ''}{(Number(entry.pnl) || 0).toFixed(2)}
                        </span>
                        {entry.rr && (
                          <span className="bg-[#F3F4F6] dark:bg-[#374151] text-[#111827] dark:text-[#E5E7EB] px-3 py-1 rounded-full text-sm font-medium">
                            R:R {entry.rr}
                          </span>
                        )}
                      </div>
                    )}
                    
                  </div>
                  
                  {/* Notes preview */}
                  {entry.notes && (
                    <div className="text-sm text-[#6B7280] dark:text-[#9CA3AF] line-clamp-2">{entry.notes}</div>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </>
  );
};

export default JournalEntryList;
